import { makeAutoObservable } from 'mobx';

import type { IAppContextProvider } from '@ifs-gcp/core';
import type { AxpxEnvironment, Language, License } from '@ifs-gcp/core/models';

import i18n from '@/app/config/i18n';
import { loadAndProcessModules } from '@/utils/processModules';

interface ILicensor {
  licenses: License[];
}

export class UserProvider implements IAppContextProvider {
  private _permissions: string[];
  private _language: Language;
  private readonly _licensor: ILicensor;
  private readonly _autoSave = true;
  private readonly _versions = {
    axpxVersion: window.APP_VERSION,
    moduleDefinitionVersion: window.MODULE_DEFINITION_VERSION,
  };

  constructor(language: string, permissions: string[] = []) {
    this._permissions = permissions;
    this._language = language;
    this._licensor = {
      licenses: [],
    };

    this.initialize();
  }

  private initialize() {
    this.setLicenses();
    makeAutoObservable(this);
  }

  private async setLicenses() {
    const standards = await loadAndProcessModules();

    /**
     * For self assessment, every module conducted is hardcoded with the license
     */
    this._licensor.licenses = Object.values(standards).map<License>((standard) => ({
      key: '',
      familyKey: standard.licenceId,
      status: 'active',
    }));
  }

  public get autoSave() {
    return this._autoSave;
  }

  public get versions() {
    return this._versions;
  }

  public get licensor() {
    return this._licensor;
  }

  public get permissions() {
    return this._permissions;
  }

  public get language(): Language {
    return this._language;
  }

  public get axpxVersion() {
    return this._versions.axpxVersion;
  }

  public get moduleDefinitionVersion() {
    return this._versions.moduleDefinitionVersion;
  }

  public set language(language: Language) {
    this._language = language;
    i18n.changeLanguage(language);
  }

  public set permissions(permissions: string[]) {
    this._permissions = permissions;
  }

  public get environment(): AxpxEnvironment {
    return import.meta.env.VITE_ENVIRONMENT as AxpxEnvironment;
  }
}
